import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { FISH_GUIDE } from '../data/fishGuide';

// Art_Velger — delt søk-og-liste-komponent for å velge fiskeart.
//
// Ekstrahert fra CaptureScreen.js (se ARKITEKTUR.md om tidligere duplisert
// SPECIES_DATA). Bruker samme Fiskearter-datasett (FISH_GUIDE) som "Den
// store kloke boken", slik at både ordinær fangstregistrering og
// Snik_Rapport bruker samme kode og samme data.
//
// Props:
// - open: boolean — om komponenten skal vises
// - onSelect(art): kalles med { navn, latin } når brukeren velger en art
// - onClose(): kalles for å lukke Art_Velger (både ved avbryt og etter valg)
// - combineWaterTypes: boolean (default false)
//     false -> uendret oppførsel: brukeren velger vanntype (salt/fersk)
//              før den søkbare listen vises.
//     true  -> viser union av salt- og ferskvannsarter i én søkbar liste,
//              uten noe vanntype-valg (Requirement 8.3).
export default function ArtVelger({ open, onSelect, onClose, combineWaterTypes = false }) {
  const [waterType, setWaterType] = useState('salt');
  const [search, setSearch] = useState('');
  const overlayRef = useRef(null);

  // Lås scroll på #root mens Art_Velger er åpen, og blokkér touch-scroll på
  // selve overlayet (samme mønster som det opprinnelige art-picker-modalet
  // i CaptureScreen.js), slik at kun listen selv kan scrolles.
  useEffect(() => {
    if (!open) return;

    const scroller = document.getElementById('root');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const prevOverflow = scroller ? scroller.style.overflow : '';
    if (scroller) {
      scroller.style.overflow = 'hidden';
    }

    const overlay = overlayRef.current;
    const handleTouch = (e) => {
      const list = overlay?.querySelector('[data-species-list]');
      if (list && list.contains(e.target)) return;
      e.preventDefault();
    };

    if (overlay) {
      overlay.addEventListener('touchmove', handleTouch, { passive: false });
    }

    return () => {
      if (scroller) {
        scroller.style.overflow = prevOverflow;
        scroller.scrollTop = scrollTop;
      }
      if (overlay) {
        overlay.removeEventListener('touchmove', handleTouch);
      }
    };
  }, [open]);

  if (!open) return null;

  const speciesList = combineWaterTypes
    ? [...FISH_GUIDE.salt, ...FISH_GUIDE.fresh]
    : (FISH_GUIDE[waterType] || []);

  const filteredSpecies = speciesList.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (s) => {
    onSelect({ navn: s.name, latin: s.latin });
    onClose();
  };

  const title = combineWaterTypes
    ? 'Velg art'
    : (waterType === 'salt' ? 'Sjøfisk' : 'Ferskvann');

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9550] bg-[#2D3436]/60 backdrop-blur-md flex items-center justify-center p-4"
    >
      <div className="w-full max-w-xl bg-[#F8F5F0] rounded-lg border-8 border-white overflow-hidden flex flex-col max-h-[85vh] shadow-2xl">
        <div className="px-8 py-8 border-b border-[#D1CCC0] flex justify-between items-center shrink-0">
          <h3 className="font-serif text-3xl font-black italic text-[#3B4D3C]">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-[#3B4D3C] hover:scale-110 transition-transform"
          >
            ✕
          </button>
        </div>

        {!combineWaterTypes && (
          <div className="px-8 pt-6 flex gap-4 shrink-0">
            <button
              onClick={() => setWaterType('salt')}
              className={`flex-1 py-4 rounded-md font-serif italic border ${
                waterType === 'salt'
                  ? 'bg-[#3B4D3C] text-white shadow-md'
                  : 'bg-[#F8F5F0] text-[#3B4D3C]/50 border-[#D1CCC0]/30'
              }`}
            >
              Saltvann
            </button>
            <button
              onClick={() => setWaterType('fresh')}
              className={`flex-1 py-4 rounded-md font-serif italic border ${
                waterType === 'fresh'
                  ? 'bg-[#3B4D3C] text-white shadow-md'
                  : 'bg-[#F8F5F0] text-[#3B4D3C]/50 border-[#D1CCC0]/30'
              }`}
            >
              Ferskvann
            </button>
          </div>
        )}

        <div className="p-6 bg-white border-b border-[#D1CCC0] shrink-0">
          <div className="flex items-center gap-4 bg-[#F8F5F0] px-6 py-4 rounded border border-[#D1CCC0] shadow-inner">
            <Search size={20} className="text-[#D1CCC0]" />
            <input
              autoFocus
              type="text"
              placeholder="Bla i katalogen..."
              className="bg-transparent w-full font-serif italic text-lg outline-none"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div
          data-species-list
          className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-3"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {filteredSpecies.map(s => (
            <div
              key={s.id || s.name}
              onClick={() => handleSelect(s)}
              className="p-6 bg-white rounded border border-[#D1CCC0] hover:bg-[#3B4D3C]/5 hover:border-[#3B4D3C]/50 cursor-pointer transition-all group flex justify-between items-center"
            >
              <div>
                <p className="font-serif font-black italic text-xl text-[#2D3436] group-hover:text-[#3B4D3C] transition-colors">
                  {s.name}
                </p>
                <p className="text-[10px] text-[#3B4D3C]/40 font-bold uppercase mt-1 italic tracking-widest">
                  {s.latin}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
